import { Flex } from "@chakra-ui/react"
import { Heading } from "DesignSystem/components"
import { useDismissibleNotice } from "Shared/hooks/useDismissibleNotice"
import { BoostingStep } from "UserCrowd/hooks/useActionSteps"
import { ROUTES } from "UserCrowd/views/routes"
import { reportErrorToSentry } from "Utilities/error"
import React from "react"
import { useNavigate } from "react-router-dom"
import { useTrackEvent } from "~/api/generated/usabilityhub-components"
import { useActionsDrawerContext } from "./ActionsDrawerContextProvider"
import { BoostingCard, BoostingCardProps } from "./BoostingCard"
import { BoostingStepKey } from "./constants"

const STEP_CONTENT: Record<
  BoostingStepKey,
  {
    title: string
    description: string
    primaryActionText: string
    secondaryActionText: string
  }
> = {
  profile: {
    title: "Strengthen your profile",
    description:
      "A stronger profile helps you get selected for more studies. Answer a few more questions to boost your chances.",
    primaryActionText: "Go to profile",
    secondaryActionText: "Do later",
  },
  linkedin_verification: {
    title: "Verify your profile with LinkedIn",
    description:
      "Connect LinkedIn to verify your background and unlock higher-paid, more relevant studies.",
    primaryActionText: "Get verified",
    secondaryActionText: "No thanks",
  },
}

export const BoostingPanel: React.FC = () => {
  const {
    boostingSteps,
    setLinkedInVerificationModalOpen,
    setLinkedInOptOutModalOpen,
  } = useActionsDrawerContext()
  const { dismiss: dismissProfileCard } = useDismissibleNotice(
    "complete_profile_card"
  )
  const navigate = useNavigate()
  const { mutate: trackEvent } = useTrackEvent()
  const applicableSteps = buildBoostingSteps(boostingSteps, {
    setLinkedInVerificationModalOpen,
    setLinkedInOptOutModalOpen,
    dismissProfileCard,
    navigate,
    trackEvent,
  })

  return (
    <>
      <Heading as="h2" textStyle="ds.heading.emphasized" mx={3}>
        Looking to unlock more earning opportunities?
      </Heading>
      <Flex
        flexDirection="column"
        gap={3}
        w="full"
        rounded="12px"
        bg="ds.background.neutral.resting"
        p={3}
      >
        {applicableSteps.map((step, index) => (
          <BoostingCard key={index} {...step} />
        ))}
      </Flex>
    </>
  )
}

const buildBoostingSteps = (
  boostingSteps: BoostingStep[],
  actions: {
    setLinkedInVerificationModalOpen: (open: boolean) => void
    setLinkedInOptOutModalOpen: (open: boolean) => void
    dismissProfileCard: () => void
    navigate: (path: string) => void
    trackEvent: (params: {
      body: { event: string; event_properties: Record<string, string> }
    }) => void
  }
): BoostingCardProps[] => {
  return boostingSteps
    .filter((step) => step.status === "incomplete")
    .map((step) => {
      const content = STEP_CONTENT[step.key]
      const stepActions = getBoostStepActions(step.key, actions)
      if (!stepActions) return null

      return {
        title: content.title,
        description: content.description,
        primaryActionText: content.primaryActionText,
        secondaryActionText: content.secondaryActionText,
        primaryAction: stepActions.onClick,
        secondaryAction: stepActions.onDismiss,
      }
    })
    .filter((step): step is BoostingCardProps => step !== null)
}

const getBoostStepActions = (
  key: BoostingStepKey,
  actions: {
    setLinkedInVerificationModalOpen: (open: boolean) => void
    setLinkedInOptOutModalOpen: (open: boolean) => void
    dismissProfileCard: () => void
    navigate: (path: string) => void
    trackEvent: (params: {
      body: { event: string; event_properties: Record<string, string> }
    }) => void
  }
) => {
  switch (key) {
    case "linkedin_verification":
      return {
        onClick: () => {
          actions.setLinkedInVerificationModalOpen(true)
          actions.trackEvent({
            body: {
              event: "panelist_linkedin_verification_modal_opened",
              event_properties: {
                source: "sidebar",
              },
            },
          })
        },
        onDismiss: () => {
          actions.setLinkedInOptOutModalOpen(true)
          actions.trackEvent({
            body: {
              event: "panelist_linkedin_verification_dismiss_modal_opened",
              event_properties: {
                source: "sidebar",
              },
            },
          })
        },
      }
    case "profile":
      return {
        onClick: () => {
          actions.navigate(ROUTES.SETTINGS.PROFILE.path)
          actions.trackEvent({
            body: {
              event: "panelist_profile_viewed",
              event_properties: { source: "sidebar" },
            },
          })
        },
        onDismiss: () => {
          actions.dismissProfileCard()
          actions.trackEvent({
            body: {
              event: "panelist_profile_dismissed",
              event_properties: {
                source: "sidebar",
              },
            },
          })
        },
      }
    default:
      reportErrorToSentry(new Error(`Unknown boosting step key: ${key}`))
      return null
  }
}
